import type {ReactNode} from 'react';
import React from 'react';
import {CUSTOM_EDIT_CONTROL_ID, CUSTOM_EDIT_CONTROL_MARGIN, EDIT_CONTROL_HEIGHT, EDIT_CONTROL_WIDTH} from './custom-edit-control.types';
import {useAppSelector} from '@/hooks';
import type {CustomItemControlPositions} from '@Components/poster-editor/components/custom-item-controls/components/single-item-custom-controls/single-item-custom-controls.types';
import {degreesToRadians} from '@Utils/math.util';
import {BaseCustomItemControl} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control';
import {ControlType} from '@Components/poster-editor/components/custom-item-controls/components/base-custom-item-control/base-custom-item-control.types';
import type {CustomItemControlsProps} from '@Components/poster-editor/components/custom-item-controls/components/custom-item-controls-wrapper/custom-item-controls-wrapper.types';
import {useSingleActivePosterItemId} from '@Hooks/poster-editor/useSingleActivePosterItemId';

export function CustomEditControl(props: CustomItemControlsProps): ReactNode | null {
  const activeItemId = useSingleActivePosterItemId();
  const activeItem = activeItemId ? window.posterEditor?.whiteboard?.getItemForId(activeItemId) : undefined;

  const arePosterItemsMoving = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsMoving[props.pageHashedId];
  });
  const arePosterItemsScaling = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsScaling[props.pageHashedId];
  });
  const arePosterItemsRotating = useAppSelector((state) => {
    return state.posterEditor.arePosterItemsRotating[props.pageHashedId];
  });

  if (!activeItem) {
    return null;
  }

  if (!activeItemId || !(activeItem.isTableItem() || activeItem.isMenu())) {
    return null;
  }

  const getEditButtonPosition = (): CustomItemControlPositions => {
    const itemHeight = props.positionProperties.scaledHeight * props.positionProperties.posterScale;
    const itemWidth = props.positionProperties.scaledWidth * props.positionProperties.posterScale;

    const leftInitial = props.positionProperties.left * props.positionProperties.posterScale;
    const topInitial = props.positionProperties.top * props.positionProperties.posterScale;

    const xCorrectionDueToItemHeight = itemHeight * Math.sin(degreesToRadians(props.positionProperties.angle));
    const xCorrectionDueToItemWidth = (itemWidth / 2) * Math.cos(degreesToRadians(props.positionProperties.angle));

    const yCorrectionDueToItemHeight = itemHeight * Math.cos(degreesToRadians(props.positionProperties.angle));
    const yCorrectionDueToItemWidth = (itemWidth / 2) * Math.sin(degreesToRadians(props.positionProperties.angle));

    const marginX = CUSTOM_EDIT_CONTROL_MARGIN * Math.sin(degreesToRadians(props.positionProperties.angle));
    const marginY = CUSTOM_EDIT_CONTROL_MARGIN * Math.cos(degreesToRadians(props.positionProperties.angle));

    const left = leftInitial - xCorrectionDueToItemHeight + xCorrectionDueToItemWidth - Math.floor(EDIT_CONTROL_WIDTH / 2) - marginX;
    const top = topInitial + yCorrectionDueToItemHeight + yCorrectionDueToItemWidth - Math.floor(EDIT_CONTROL_HEIGHT / 2) + marginY;

    return {left, top, angle: props.positionProperties.angle};
  };

  const getEditButton = (): ReactNode | null => {
    const position = getEditButtonPosition();

    return (
      <BaseCustomItemControl
        icon={'icon-pencil'}
        text={window.i18next.t('pmwjs_edit')}
        controlId={CUSTOM_EDIT_CONTROL_ID}
        top={position.top}
        left={position.left}
        angle={position.angle}
        borderRadius={'6px'}
        isVisible={!arePosterItemsMoving && !arePosterItemsRotating && !arePosterItemsScaling}
        onPointerDown={() => {
          activeItem.editItem();
        }}
        controlType={ControlType.CLICK}
      />
    );
  };

  if (arePosterItemsMoving || arePosterItemsRotating || arePosterItemsScaling) {
    return null;
  }

  return getEditButton();
}
